import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, ChevronLeft, ChevronRight, BadgeCheck, ExternalLink } from 'lucide-react';

const GOOGLE_PROFILE_URL = 'https://www.google.com/maps/search/?api=1&query=Aquarist+Pools+and+Fitness&query_place_id=ChIJ6RS9ynVtrjsRm_XGFKvrWmM';
const GOOGLE_REVIEW_URL = 'https://g.page/r/CZv1xhSr61pjEBM/review';

const GoogleGIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"/>
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"/>
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/>
  </svg>
);

const TESTIMONIALS = [
  {
    name: 'Sachin Kuchekar',
    avatar: '/assets/testimonials/sachin-kuchekar.png',
    rating: 5,
    text: 'Excellent service, very professional. Owner understands the requirements perfectly. Really happy with the services and would definitely recommend them.',
  },
  {
    name: 'Aarushi B',
    avatar: '/assets/testimonials/aarushi-b.png',
    rating: 5,
    text: "Got a swimming pool built at my farmhouse, and I'm really happy with the work. The finishing is neat, the design looks great, and the whole area feels like a proper resort now. The team was very supportive, professional, and excellent at their work.",
  },
  {
    name: 'Anusha Tripathi',
    avatar: '/assets/testimonials/anusha-tripathi.png',
    rating: 5,
    text: "Great service and very professional. I'm definitely coming back again.",
  },
  {
    name: 'Charan Reddy',
    avatar: '/assets/testimonials/charan-reddy.png',
    rating: 5,
    text: null,
  },
];

const TestimonialCard = ({ review }) => (
  <figure
    className="h-full bg-white rounded-2xl shadow-lg shadow-[#003366]/5 border border-gray-100 p-6 sm:p-8 flex flex-col"
    data-testid={`testimonial-card-${review.name.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center justify-between mb-4">
      <div className="flex gap-0.5" aria-label={`${review.rating} out of 5 stars`}>
        {Array.from({ length: review.rating }).map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
        ))}
      </div>
      <GoogleGIcon className="w-5 h-5" />
    </div>

    {review.text ? (
      <blockquote className="flex-1">
        <p className="text-gray-700 text-sm sm:text-base leading-relaxed">"{review.text}"</p>
      </blockquote>
    ) : (
      <div className="flex-1" />
    )}

    <figcaption className="flex items-center gap-4 mt-6 pt-5 border-t border-gray-100">
      <img
        src={review.avatar}
        alt={`${review.name} profile photo`}
        width="56"
        height="56"
        loading="lazy"
        decoding="async"
        className="w-14 h-14 rounded-full object-cover ring-2 ring-[#00AEEF]/20 flex-shrink-0"
      />
      <div className="min-w-0">
        <p className="font-bold text-[#003366] truncate">{review.name}</p>
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
          <BadgeCheck className="w-3.5 h-3.5 text-[#00AEEF]" />
          Verified Google Review
        </p>
      </div>
    </figcaption>
  </figure>
);

const Testimonials = () => {
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(1);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  const maxIndex = TESTIMONIALS.length - perView;

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setPerView(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  useEffect(() => {
    if (index > maxIndex) setIndex(Math.max(0, maxIndex));
  }, [maxIndex, index]);

  const next = useCallback(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), [maxIndex]);
  const prev = useCallback(() => setIndex((i) => (i <= 0 ? maxIndex : i - 1)), [maxIndex]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 6000);
    return () => clearInterval(t);
  }, [next, paused]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  return (
    <section
      className="py-20 bg-gradient-to-b from-gray-50 to-white"
      data-testid="testimonials-section"
      aria-label="Client testimonials"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#00AEEF]/10 text-[#0077a8] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <GoogleGIcon className="w-4 h-4" />
            <span className="flex gap-0.5" aria-hidden="true">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[#FBBC05] text-[#FBBC05]" />
              ))}
            </span>
            Verified Google Reviews
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#003366] mb-3">What Our Clients Say</h2>
          <p className="text-lg text-gray-600">Real experiences from our valued clients.</p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
            >
              {TESTIMONIALS.map((review) => (
                <div
                  key={review.name}
                  className="flex-shrink-0 px-2 sm:px-3"
                  style={{ width: `${100 / perView}%` }}
                >
                  <TestimonialCard review={review} />
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={prev}
            aria-label="Previous testimonial"
            data-testid="testimonial-prev-btn"
            className="hidden sm:flex absolute -left-4 lg:-left-6 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white shadow-lg border border-gray-100 text-[#003366] hover:bg-[#00AEEF] hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            aria-label="Next testimonial"
            data-testid="testimonial-next-btn"
            className="hidden sm:flex absolute -right-4 lg:-right-6 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-white shadow-lg border border-gray-100 text-[#003366] hover:bg-[#00AEEF] hover:text-white transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Testimonial navigation">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  i === index ? 'w-8 bg-[#00AEEF]' : 'w-2.5 bg-gray-300 hover:bg-[#00AEEF]/50'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <a
            href={GOOGLE_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-more-google-reviews-btn"
            className="inline-flex items-center gap-2 bg-[#003366] hover:bg-[#002244] text-white font-semibold px-8 py-3.5 rounded-full transition-all duration-300 hover:scale-105"
          >
            <GoogleGIcon className="w-4 h-4" />
            See More Reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-your-experience-btn"
            className="inline-flex items-center gap-2 text-[#00AEEF] hover:text-[#003366] font-semibold px-6 py-3 transition-colors"
          >
            Share Your Experience
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
